/**
 * ConfigService - Runtime Configuration Management
 *
 * Manages application configuration with two-tier strategy:
 * 1. Runtime config (priority) - Generated at container start via generate-config.sh
 * 2. Build-time config (fallback) - From .env files during development
 *
 * This enables single Docker image deployments across multiple environments.
 */

interface AppConfig {
  API_HOST: string;
  REDIRECT_URI?: string;
  SESSION_INACTIVITY_MS?: number;
}

interface WindowWithConfig extends Window {
  APP_CONFIG?: AppConfig;
  configPromise?: Promise<AppConfig | null>;
}

class ConfigService {
  private static instance: ConfigService;
  private config: AppConfig | null = null;
  private initialized = false;
  private static readonly DEFAULT_SESSION_INACTIVITY_MS = 15 * 60 * 1000;
  // Placeholder API host used ONLY when running under Vitest (MODE === 'test')
  // and VITE_API_BASE_URL is not provided (e.g. CI runners without a .env file).
  private static readonly TEST_API_HOST = 'http://localhost:3000';

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  /**
   * Initialize configuration (async)
   * Should be called during app bootstrap in main.tsx
   * Waits for window.configPromise to resolve
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    const windowWithConfig = window as WindowWithConfig;

    if (windowWithConfig.configPromise) {
      try {
        const runtimeConfig = await windowWithConfig.configPromise;

        if (runtimeConfig?.API_HOST) {
          this.config = {
            ...runtimeConfig,
            SESSION_INACTIVITY_MS: this.parseOptionalPositiveInt(
              runtimeConfig.SESSION_INACTIVITY_MS
            ),
          };
        } else {
          this.useBuildTimeConfig();
        }
      } catch (error) {
        console.error('Error loading runtime config:', error);
        this.useBuildTimeConfig();
      }
    } else {
      // No configPromise available (development mode)
      this.useBuildTimeConfig();
    }

    this.initialized = true;
  }

  /**
   * Fallback to build-time environment variables
   * Used during local development with npm run dev
   */
  private useBuildTimeConfig(): void {
    const isTestEnv = import.meta.env.MODE === 'test';
    const apiHost: string | undefined =
      import.meta.env.VITE_API_BASE_URL ||
      (isTestEnv ? ConfigService.TEST_API_HOST : undefined);

    if (!apiHost) {
      throw new Error(
        'Missing VITE_API_BASE_URL environment variable. ' +
          'Please ensure .env file exists with VITE_API_BASE_URL defined.'
      );
    }

    this.config = {
      API_HOST: apiHost,
      REDIRECT_URI: import.meta.env.VITE_REDIRECT_URI,
      SESSION_INACTIVITY_MS: this.parseOptionalPositiveInt(
        import.meta.env.VITE_SESSION_INACTIVITY_MS
      ),
    };
  }

  private parseOptionalPositiveInt(value: unknown): number | undefined {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    const parsed = Number.parseInt(String(value), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  private resolveSessionInactivityMs(config?: AppConfig | null): number {
    const runtimeValue = config?.SESSION_INACTIVITY_MS;
    if (
      typeof runtimeValue === 'number' &&
      Number.isFinite(runtimeValue) &&
      runtimeValue > 0
    ) {
      return runtimeValue;
    }

    const buildTimeValue = this.parseOptionalPositiveInt(
      import.meta.env.VITE_SESSION_INACTIVITY_MS
    );
    if (buildTimeValue) {
      return buildTimeValue;
    }

    return ConfigService.DEFAULT_SESSION_INACTIVITY_MS;
  }

  /**
   * Synchronous fallback initialization
   * Used when config is accessed before async initialize() completes
   */
  private autoInitialize(): void {
    if (this.initialized) return;

    console.warn(
      'ConfigService accessed before initialize() - using build-time config'
    );
    this.useBuildTimeConfig();
    this.initialized = true;
  }

  /**
   * Get API host URL
   * Returns the base URL for API requests
   */
  public getApiHost(): string {
    if (!this.initialized) {
      this.autoInitialize();
    }

    if (!this.config?.API_HOST) {
      throw new Error('API_HOST not configured');
    }

    return this.config.API_HOST;
  }

  /**
   * Idle timeout before the inactivity dialog is shown (milliseconds).
   */
  public getSessionInactivityMs(): number {
    if (!this.initialized) {
      this.autoInitialize();
    }

    return this.resolveSessionInactivityMs(this.config);
  }

  /**
   * Get entire configuration object (immutable copy)
   */
  public getConfig(): Readonly<AppConfig> {
    if (!this.initialized) {
      this.autoInitialize();
    }

    if (!this.config) {
      throw new Error('Configuration not initialized');
    }

    return { ...this.config };
  }

  /**
   * Check if configuration is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }
}

// Export singleton instance
export const configService = ConfigService.getInstance();
